import React, { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';

/**
 * BD-10 — the firm's house rules for follow-up drafts (admin), "configurable per
 * user and per firm". GET / PUT /api/bd-assistant/admin/followup-rules.
 *
 *   banned phrases  one per line. Until saved, the default list applies (Ryan's);
 *                   "Use the default list" goes back to it
 *   standing CC     copied on every follow-up in the firm
 *   tone notes      the firm's house style; each person's own notes come first
 *
 * Each person adds their own on top in their BD Assistant settings. Saved with
 * one button, since it changes every draft in the firm.
 */

interface Rules {
  banned_phrases: string[];
  banned_phrases_source: 'firm' | 'default';
  default_phrases: string[];
  default_cc: string[];
  tone_notes: string | null;
}

const INPUT_CLS = 'w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50';
const lines = (s: string) => s.split('\n').map((l) => l.trim()).filter(Boolean);
const commas = (s: string) => s.split(',').map((l) => l.trim()).filter(Boolean);

const BDFollowUpFirmRulesCard: React.FC = () => {
  const [rules, setRules] = useState<Rules | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [phrases, setPhrases] = useState('');
  const [cc, setCc] = useState('');
  const [tone, setTone] = useState('');

  const show = (r: Rules) => {
    setRules(r);
    setPhrases(r.banned_phrases.join('\n'));
    setCc(r.default_cc.join(', '));
    setTone(r.tone_notes || '');
  };

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const { data } = await api.get('/api/bd-assistant/admin/followup-rules');
      show(data.rules);
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (body: Record<string, unknown>, done: string) => {
    setSaving(true);
    try {
      const { data } = await api.put('/api/bd-assistant/admin/followup-rules', body);
      show(data.rules);
      toast.success(done);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not save — try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <div className="text-sm text-gray-600" data-testid="bd-firm-rules-error">
        Could not load the firm house rules.{' '}
        <button type="button" className="text-blue-600 underline" onClick={load}>Retry</button>
      </div>
    );
  }
  if (!rules) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="bd-firm-rules-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading the firm house rules…
      </div>
    );
  }

  const changed = phrases !== rules.banned_phrases.join('\n') || cc !== rules.default_cc.join(', ') || tone !== (rules.tone_notes || '');
  return (
    <div className="space-y-4" data-testid="bd-firm-rules">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Follow-up emails: firm house rules</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Apply to every follow-up and thread reply drafted for anyone in the firm. Each person adds their own in their BD Assistant settings.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-900">Phrases the firm never uses</label>
        <p className="text-xs text-gray-500 mb-1" data-testid="bd-firm-phrases-source">
          One per line. {rules.banned_phrases_source === 'default' ? 'Using the default list.' : 'The firm’s own list.'}
          {rules.banned_phrases_source === 'firm' && (
            <>
              {' '}
              <button
                type="button" className="text-blue-600 underline disabled:opacity-50" disabled={saving}
                onClick={() => save({ banned_phrases: null }, 'Back to the default list.')} data-testid="bd-firm-phrases-default"
              >
                Use the default list
              </button>
            </>
          )}
        </p>
        <textarea rows={6} value={phrases} disabled={saving} onChange={(e) => setPhrases(e.target.value)} className={INPUT_CLS} data-testid="bd-firm-phrases" />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-900">Standing CC for the firm</label>
        <p className="text-xs text-gray-500 mb-1">Comma-separated. Copied on every follow-up, on top of each person’s own CC list.</p>
        <input type="text" value={cc} disabled={saving} onChange={(e) => setCc(e.target.value)} className={INPUT_CLS} data-testid="bd-firm-cc" />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-900">Firm house style</label>
        <p className="text-xs text-gray-500 mb-1">Tone notes every draft follows, after the person’s own. e.g. Plain English. No exclamation marks.</p>
        <textarea rows={3} maxLength={1000} value={tone} disabled={saving} onChange={(e) => setTone(e.target.value)} className={INPUT_CLS} data-testid="bd-firm-tone" />
      </div>

      <button
        type="button"
        disabled={saving || !changed}
        onClick={() => save({ banned_phrases: lines(phrases), default_cc: commas(cc), tone_notes: tone }, 'Firm house rules saved.')}
        className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
        data-testid="bd-firm-save"
      >
        {saving ? 'Saving…' : 'Save firm rules'}
      </button>
    </div>
  );
};

export default BDFollowUpFirmRulesCard;
